import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Layers, CheckCircle2, ChevronRight } from 'lucide-react';
import { ICON_PX } from '../../ui';

/**
 * Sub-process node — represents a nested/linked workflow.
 * Shows a stacked-layers icon and the linked workflow name.
 * Expandable in future versions.
 */
function SubProcessNode({ data, selected }) {
  const { label, status = 'pending', linkedWorkflowId } = data;
  const isSatisfied = status === 'satisfied';
  const isAvailable = status === 'available';

  return (
    <>
      <Handle type="target" position={Position.Top} className="!bg-[var(--slate)] !w-2.5 !h-2.5 !border-2 !border-white !rounded-full" />

      <div
        className={[
          'rounded-xl border-2 px-4 py-3 min-w-[200px] max-w-[260px] transition-all',
          isSatisfied ? 'bg-green-bg border-[var(--green)] shadow-sm' :
          isAvailable ? 'bg-[var(--aqua)]/5 border-[var(--aqua)] shadow-md' :
          'bg-white border-slate-200 shadow-sm',
          selected ? 'ring-2 ring-aqua ring-offset-2' : '',
          isAvailable ? 'cursor-pointer hover:shadow-lg' : '',
        ].join(' ')}
      >
        {/* Type badge */}
        <div className={[
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold mb-2 uppercase tracking-wide',
          isSatisfied ? 'bg-[var(--green)]/10 text-green-nn' :
          isAvailable ? 'bg-[var(--aqua)]/10 text-[var(--aqua)]' :
          'bg-slate-100 text-slate-400',
        ].join(' ')}>
          <Layers size={10} />
          Sub-process
        </div>

        {/* Name + drill-in affordance */}
        <div className="flex items-start gap-2">
          {isSatisfied ? (
            <CheckCircle2 size={ICON_PX.md} className="text-green-nn shrink-0 mt-0.5" />
          ) : (
            <Layers size={ICON_PX.md} className={`${isAvailable ? 'text-[var(--aqua)]' : 'text-slate-400'} shrink-0 mt-0.5`} />
          )}
          <span className={`text-sm font-medium leading-snug flex-1 min-w-0 truncate ${
            isSatisfied ? 'text-green-nn' : 'text-text-dark'
          }`}>
            {label || 'Sub-process'}
          </span>
          <ChevronRight size={ICON_PX.sm} className="text-slate-300 shrink-0 mt-0.5" />
        </div>

        {/* Linked workflow hint */}
        {linkedWorkflowId && (
          <div className="text-[10px] text-slate-400 mt-1.5 truncate">
            Linked: {linkedWorkflowId}
          </div>
        )}
      </div>

      <Handle type="source" position={Position.Bottom} className="!bg-[var(--slate)] !w-2.5 !h-2.5 !border-2 !border-white !rounded-full" />
    </>
  );
}

export default memo(SubProcessNode);
